import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  MdDashboard,
  MdInventory,
  MdCategory,
  MdWarning,
  MdRemoveShoppingCart,
  MdAttachMoney,
  MdShoppingCart,
  MdTrendingUp,
  MdArrowUpward,
  MdAutoAwesome,
  MdRefresh,
  MdCalendarToday,
  MdStorefront,
  MdArrowForward,
} from "react-icons/md";

import heroImage from "../../assets/image/crm-dashboard-hero.png";

// TODO: apni actual Inventory dashboard service import karo
// import {
//   getInventoryDashboard,
//   getTodayInventoryDashboard,
// } from "../../../redux/services/inventoryDashboardServices/inventoryDashboard.Services";

const iconMap = {
  dashboard: MdDashboard,
  inventory: MdInventory,
  category: MdCategory,
  warning: MdWarning,
  outOfStock: MdRemoveShoppingCart,
  revenue: MdAttachMoney,
  purchase: MdShoppingCart,
};

const accentMap = {
  cyan: "#22d3ee",
  amber: "#fbbf24",
  violet: "#a78bfa",
  rose: "#fb7185",
  indigo: "#818cf8",
  emerald: "#34d399",
  sky: "#38bdf8",
  red: "#f87171",
};

const InventoryDashboard = () => {
  // ✅ Redux auth se vendor info lo
  const {
    admin,
    category_id,
    subcategory_id,
    subcategory_slug,
    permissions,
  } = useSelector((state) => state.auth);

  const vendorName =
    admin?.business_name ||
    admin?.name ||
    admin?.username ||
    "Vendor";

  // ✅ Category/Subcategory display names (agar auth mein ho)
  const displayCategory =
    admin?.category?.name ||
    admin?.category_name ||
    category_id
      ? `Category #${category_id}`
      : "Inventory";

  const [summary, setSummary] = useState({});
  const [todaySummary, setTodaySummary] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("total");

  const fetchDashboard = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);

      /*
       * TODO: API connect karo
       *
       * const [dashboardRes, todayRes] = await Promise.all([
       *   getInventoryDashboard(),
       *   getTodayInventoryDashboard(),
       * ]);
       * setSummary(dashboardRes?.data || {});
       * setTodaySummary(todayRes?.data || {});
       */

      setSummary({});
      setTodaySummary({});
    } catch (error) {
      console.error("Inventory dashboard error:", error);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  /* ============================================================
     KPI Tiles — Overall
     ============================================================ */
  const totalTiles = useMemo(
    () => [
      {
        icon: "inventory",
        label: "Total Products",
        href: "/product-list",
        points: summary?.total_products || 0,
        accent: "cyan",
        helper: "All products",
      },
      {
        icon: "category",
        label: "Total Categories",
        href: "/categories-list",
        points: summary?.total_categories || 0,
        accent: "violet",
        helper: "Product categories",
      },
      {
        icon: "warning",
        label: "Low Stock",
        href: "/product-list",
        points: summary?.low_stock || 0,
        accent: "amber",
        helper: "Need restocking",
      },
      {
        icon: "outOfStock",
        label: "Out of Stock",
        href: "/product-list",
        points: summary?.out_of_stock || 0,
        accent: "rose",
        helper: "Currently unavailable",
      },
      {
        icon: "purchase",
        label: "Total Purchases",
        href: "/purchase-list",
        points: summary?.total_purchases || 0,
        accent: "indigo",
        helper: "Purchase transactions",
      },
      {
        icon: "revenue",
        label: "Stock Value",
        href: "/product-list",
        points: summary?.stock_value || 0,
        accent: "emerald",
        helper: "Current inventory value",
      },
    ],
    [summary]
  );

  /* ============================================================
     KPI Tiles — Today
     ============================================================ */
  const todayTiles = useMemo(
    () => [
      {
        icon: "inventory",
        label: "Today's Products",
        href: "/product-list",
        points: todaySummary?.today_products || 0,
        accent: "cyan",
        helper: "Products added today",
      },
      {
        icon: "purchase",
        label: "Today's Purchases",
        href: "/purchase-list",
        points: todaySummary?.today_purchases || 0,
        accent: "indigo",
        helper: "Purchased today",
      },
      {
        icon: "warning",
        label: "Low Stock",
        href: "/product-list",
        points: todaySummary?.today_low_stock || 0,
        accent: "amber",
        helper: "Stock alerts",
      },
      {
        icon: "outOfStock",
        label: "Out of Stock",
        href: "/product-list",
        points: todaySummary?.today_out_of_stock || 0,
        accent: "rose",
        helper: "Unavailable products",
      },
    ],
    [todaySummary]
  );

  const displayedTiles = activeTab === "total" ? totalTiles : todayTiles;

  const totalProducts = Number(summary?.total_products || 0);
  const lowStock = Number(summary?.low_stock || 0);
  const outOfStock = Number(summary?.out_of_stock || 0);

  const stockAlertRate = totalProducts
    ? (((lowStock + outOfStock) / totalProducts) * 100).toFixed(1)
    : "0.0";

  const availableRate = totalProducts
    ? (((totalProducts - outOfStock) / totalProducts) * 100).toFixed(1)
    : "0.0";

  /* ============================================================
     Charts
     ============================================================ */
  const stockChartOptions = {
    labels: ["Available", "Low Stock", "Out of Stock"],
    colors: ["#34d399", "#fbbf24", "#fb7185"],
    chart: {
      background: "transparent",
      toolbar: { show: false },
      foreColor: "#94a3b8",
    },
    legend: {
      position: "bottom",
      labels: { colors: "#cbd5e1" },
      markers: { radius: 4 },
    },
    stroke: { show: false },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: { donut: { size: "72%" } },
    },
    tooltip: { theme: "dark" },
  };

  const stockChartSeries = [
    Number(summary?.available_stock || 0),
    Number(summary?.low_stock || 0),
    Number(summary?.out_of_stock || 0),
  ];

  const movementOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      foreColor: "#94a3b8",
      background: "transparent",
    },
    colors: ["#22d3ee", "#818cf8", "#34d399"],
    plotOptions: {
      bar: {
        columnWidth: "42%",
        borderRadius: 6,
        borderRadiusApplication: "end",
      },
    },
    grid: {
      borderColor: "rgba(148,163,184,.12)",
      strokeDashArray: 4,
    },
    xaxis: {
      categories:
        summary?.inventoryPerformance?.length > 0
          ? summary.inventoryPerformance.map((item) => item.month)
          : ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      labels: {
        style: { colors: "#94a3b8", fontSize: "11px" },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { style: { colors: "#64748b" } },
    },
    legend: {
      position: "bottom",
      labels: { colors: "#cbd5e1" },
      markers: { radius: 4 },
    },
    tooltip: { theme: "dark" },
    dataLabels: { enabled: false },
  };

  const movementSeries = [
    {
      name: "Purchase",
      data:
        summary?.inventoryPerformance?.length > 0
          ? summary.inventoryPerformance.map((item) => item.purchase)
          : [120, 150, 180, 140, 210, 190],
    },
    {
      name: "Sales",
      data:
        summary?.inventoryPerformance?.length > 0
          ? summary.inventoryPerformance.map((item) => item.sales)
          : [90, 120, 150, 110, 180, 160],
    },
    {
      name: "Stock Added",
      data:
        summary?.inventoryPerformance?.length > 0
          ? summary.inventoryPerformance.map((item) => item.stock_added)
          : [30, 40, 50, 30, 45, 35],
    },
  ];

  const hour = new Date().getHours();
  const greeting =
    hour < 12
      ? "Good morning"
      : hour < 18
      ? "Good afternoon"
      : "Good evening";

  /* ============================================================
     Loading
     ============================================================ */
  if (isLoading) {
    return (
      <div className="crm-dashboard-loading">
        <div className="crm-loader-ring" />
        <strong>Preparing your inventory workspace</strong>
        <span>Loading inventory data…</span>
      </div>
    );
  }

  /* ============================================================
     Main Render
     ============================================================ */
  return (
    <div className="crm-dashboard">
      {/* ===================== HERO ===================== */}
      <section className="crm-dashboard-hero">
        <img src={heroImage} alt="Inventory dashboard" />
        <div className="crm-hero-glow" />

        <div className="crm-hero-content">
          <div className="crm-ai-badge">
            <MdStorefront size={15} />
            {subcategory_slug
              ? `${subcategory_slug.replace(/-/g, " ").toUpperCase()} • INVENTORY`
              : "INVENTORY MANAGEMENT"}
          </div>

          <h1>
            {greeting}, <span>{vendorName}</span>
          </h1>

          <p>
            Monitor your products, stock levels, and inventory performance
            in one focused workspace.
          </p>

          <div className="crm-hero-meta">
            <span>
              <MdCalendarToday size={15} />{" "}
              {new Date().toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>

            <button
              onClick={() => fetchDashboard(true)}
              disabled={refreshing}
            >
              <MdRefresh
                className={refreshing ? "crm-spin" : ""}
                size={16}
              />
              {refreshing ? "Refreshing" : "Refresh data"}
            </button>
          </div>
        </div>

        <div className="crm-hero-stats">
          <div>
            <span>Available stock</span>
            <strong>{availableRate}%</strong>
            <small>
              <MdArrowUpward /> Current availability
            </small>
          </div>

          <div>
            <span>Stock alerts</span>
            <strong>{stockAlertRate}%</strong>
            <small>
              <MdTrendingUp /> Requires attention
            </small>
          </div>
        </div>
      </section>

      {/* ===================== SECTION HEADER ===================== */}
      <section className="crm-section-head">
        <div>
          <span className="crm-eyebrow">Inventory overview</span>
          <h2>
            {activeTab === "total"
              ? "Inventory snapshot"
              : "Today's snapshot"}
          </h2>
        </div>

        <div className="crm-view-switch">
          <button
            className={activeTab === "total" ? "active" : ""}
            onClick={() => setActiveTab("total")}
          >
            Overall
          </button>
          <button
            className={activeTab === "today" ? "active" : ""}
            onClick={() => setActiveTab("today")}
          >
            Today
          </button>
        </div>
      </section>

      {/* ===================== KPI CARDS ===================== */}
      <section className="crm-kpi-grid">
        {displayedTiles.map((item, index) => {
          const IconComponent = iconMap[item.icon] || MdDashboard;
          const accent = accentMap[item.accent] || accentMap.cyan;

          return (
            <Link
              to={item.href}
              className="crm-kpi-card"
              key={`${item.label}-${index}`}
              style={{ "--accent": accent }}
            >
              <div className="crm-kpi-top">
                <div className="crm-kpi-icon">
                  <IconComponent size={21} />
                </div>
                <MdArrowForward className="crm-kpi-arrow" />
              </div>

              <div className="crm-kpi-value">
                {Number(item.points || 0).toLocaleString("en-IN")}
              </div>

              <div className="crm-kpi-label">{item.label}</div>
              <div className="crm-kpi-helper">{item.helper}</div>
              <div className="crm-kpi-line" />
            </Link>
          );
        })}
      </section>

      {/* ===================== CHARTS ===================== */}
      <section className="crm-chart-grid">
        {/* Stock Status */}
        <div className="crm-chart-card crm-source-card">
          <div className="crm-card-heading">
            <div>
              <span className="crm-eyebrow">Stock intelligence</span>
              <h3>Stock status</h3>
            </div>
            <span className="crm-live-dot">LIVE</span>
          </div>

          <div className="crm-chart-body">
            {stockChartSeries.some((value) => value > 0) ? (
              <Chart
                options={stockChartOptions}
                series={stockChartSeries}
                type="donut"
                height={310}
              />
            ) : (
              <div className="crm-empty-chart">
                <MdInventory size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                <p>No stock data available yet</p>
                <small>Add products to see stock insights</small>
              </div>
            )}
          </div>
        </div>

        {/* Inventory Movement */}
        <div className="crm-chart-card">
          <div className="crm-card-heading">
            <div>
              <span className="crm-eyebrow">Inventory analytics</span>
              <h3>Purchase &amp; Sales</h3>
            </div>
            <span className="crm-period-chip">Current data</span>
          </div>

          <div className="crm-chart-body">
            <Chart
              options={movementOptions}
              series={movementSeries}
              type="bar"
              height={310}
            />
          </div>
        </div>
      </section>
    </div>
  );
};
export default InventoryDashboard;